import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowDown } from '@ankr.com/ui';
import { Chain, ChainPath } from '@ankr.com/chains-list';
import { UserEndpointToken } from 'multirpc-sdk';
import { useMemo } from 'react';

import { Checkbox } from 'modules/common/components/Checkbox';
import { JWT } from 'domains/jwtToken/types';
import { SubchainSelectors } from 'domains/projects/components/ChainCellWithSubchains/SubchainSelectors';
import { getDisabledChainTooltipText } from 'modules/chains/utils/getDisabledChainTooltipText';
import { mapProjectChains } from 'domains/projects/screens/ProjectPage/components/ProjectChainsTable/hooks/useProjectChains';
import { selectAllPathsByChainId } from 'modules/chains/store/selectors';
import { useAppSelector } from 'store/useAppSelector';
import { useTranslation } from 'modules/i18n/hooks/useTranslation';

import { IProjectSubchains } from '../../hooks/useProjectSubchains';
import { chainProjectItemTranslation } from '../../translation';
import { useChainProjectAccordion } from './useChainProjectAccordion';
import { useChainProjectAccordionStyles } from './useChainProjectAccordionStyles';

export interface IChainProjectAccordionProps {
  chain: Chain;
  handleProjectChange: (projectToken: string) => void;
  isLoadingAddChainsToProject: boolean;
  isLoadingAddChainsToProjects?: boolean;
  project: JWT;
  selectedProjectsSubchains: IProjectSubchains;
  setSelectedSubchains: (
    subchains: ChainPath[],
    projectToken: UserEndpointToken,
  ) => void;
  expandedId?: UserEndpointToken;
  onToggleAccordion: (token: UserEndpointToken) => void;
}

export const ChainProjectAccordion = ({
  chain,
  expandedId,
  handleProjectChange,
  isLoadingAddChainsToProject,
  isLoadingAddChainsToProjects,
  onToggleAccordion,
  project,
  selectedProjectsSubchains,
  setSelectedSubchains,
}: IChainProjectAccordionProps) => {
  const allChainPaths = useAppSelector(state =>
    selectAllPathsByChainId(state, chain.id),
  );
  const { name, userEndpointToken } = project;

  const {
    isIndeterminate,
    isSelected,
    onAccordionClick,
    onProjectChange,
    selectedChainPaths,
    setSelectedChainPaths,
  } = useChainProjectAccordion({
    chain,
    handleProjectChange,
    isLoadingAddChainsToProjects,
    onToggleAccordion,
    selectedProjectsSubchains,
    setSelectedSubchains,
    userEndpointToken,
  });

  const { classes } = useChainProjectAccordionStyles();

  const { keys, t } = useTranslation(chainProjectItemTranslation);

  const isLastChainInProject = useMemo(
    () =>
      (isSelected || isIndeterminate) &&
      selectedProjectsSubchains[userEndpointToken]?.length <=
        allChainPaths.length &&
      selectedProjectsSubchains[userEndpointToken]?.every(path =>
        allChainPaths.includes(path),
      ),
    [
      allChainPaths,
      isIndeterminate,
      isSelected,
      selectedProjectsSubchains,
      userEndpointToken,
    ],
  );

  const isCheckboxDisabled =
    isLoadingAddChainsToProject ||
    isLoadingAddChainsToProjects ||
    isLastChainInProject;

  if (allChainPaths.length === 1) {
    return (
      <Tooltip
        placement="left"
        title={getDisabledChainTooltipText(isLastChainInProject)}
      >
        <div className={classes.singleProjectItem}>
          <Checkbox
            isDisabled={isCheckboxDisabled}
            hasPadding
            isChecked={isSelected}
            isIndeterminate={isIndeterminate}
            label={name || t(keys.defaultName)}
            onChange={onProjectChange}
            classNameLabel={classes.projectLabel}
          />
        </div>
      </Tooltip>
    );
  }

  const isLastPathSelected =
    selectedProjectsSubchains[userEndpointToken]?.length === 1;

  return (
    <Accordion
      expanded={expandedId === userEndpointToken}
      className={classes.chainProjectAccordion}
    >
      <AccordionSummary
        onClick={onAccordionClick}
        expandIcon={<ArrowDown />}
        className={classes.chainProjectAccordionSummary}
      >
        <Tooltip
          placement="left"
          title={getDisabledChainTooltipText(isLastChainInProject)}
        >
          <div className={classes.projectItemWrapper}>
            <Checkbox
              isDisabled={isCheckboxDisabled}
              hasPadding
              isChecked={isSelected}
              isIndeterminate={isIndeterminate}
              label={name || t(keys.defaultName)}
              onChange={onProjectChange}
              classNameLabel={classes.projectLabel}
            />
          </div>
        </Tooltip>
      </AccordionSummary>
      <AccordionDetails className={classes.chainProjectCellAccordionDetails}>
        <Typography variant="subtitle3" color="textSecondary">
          {t(keys.networks)}
        </Typography>
        <SubchainSelectors
          isLoading={isLoadingAddChainsToProjects}
          chain={mapProjectChains(chain, true)}
          setSelectedChainPaths={setSelectedChainPaths}
          selectedChainPaths={selectedChainPaths}
          isLastChainInProject={isLastChainInProject}
          isLastPathSelected={isLastPathSelected}
        />
      </AccordionDetails>
    </Accordion>
  );
};
